import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { prestationsOrganisme } from '../data/mockData';
import { Clock, Euro, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

const PrestationDetail = () => {
  const { slug } = useParams();
  const category = prestationsOrganisme.find(cat => cat.slug === slug);

  if (!category) {
    return <Navigate to="/prestations" replace />;
  }

  return (
    <div>
      {/* Hero Section */}
      <div 
        className="relative h-[400px] flex items-center justify-center text-white"
        style={{
          backgroundImage: `url(${category.image}?w=1920&h=400&fit=crop)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/70 to-black/80"></div>
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="mb-4 text-sm flex items-center gap-2">
            <Link to="/" className="text-gray-300 hover:text-white transition-colors">
              Accueil
            </Link>
            <span className="text-red-500">/</span>
            <Link to="/prestations" className="text-gray-300 hover:text-white transition-colors">
              Prestations
            </Link>
            <span className="text-red-500">/</span>
            <span className="text-red-500">{category.title}</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 uppercase tracking-wide">
            {category.title}
          </h1>
          
          <p className="text-lg md:text-xl text-gray-200">
            {category.description}
          </p>
        </div>
      </div>

      {/* Back Button */}
      <div className="bg-gray-50 py-6">
        <div className="container mx-auto px-4">
          <Link 
            to="/prestations" 
            className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold transition-colors"
          >
            <ArrowLeft size={20} />
            Retour aux prestations
          </Link>
        </div>
      </div>

      {/* Formations List */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="mb-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Prestations {category.title}
              </h2>
              <p className="text-gray-600 text-lg">
                {category.formationsCount} prestation{category.formationsCount > 1 ? 's' : ''} disponible{category.formationsCount > 1 ? 's' : ''}
              </p>
            </div>

            <div className="space-y-6">
              {category.formations.map((formation) => (
                <div 
                  key={formation.id}
                  className={`bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border-l-4 ${
                    formation.available === false ? 'border-gray-400 opacity-75' : 'border-red-600'
                  }`}
                >
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          {formation.available === false ? (
                            <XCircle size={24} className="text-gray-400 flex-shrink-0 mt-1" />
                          ) : (
                            <CheckCircle size={24} className="text-green-600 flex-shrink-0 mt-1" />
                          )}
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                              {formation.name}
                            </h3>
                            {formation.available === false && (
                              <span className="inline-block bg-gray-200 text-gray-700 text-xs px-3 py-1 rounded-full font-semibold">
                                Indisponible
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-6 ml-9">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Clock size={18} className="text-red-600" />
                            <span className="text-sm font-medium">{formation.duration}</span>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <span className="text-lg font-semibold text-red-600">Tarif sur devis</span>
                          </div>
                        </div>
                      </div>
                      
                      {formation.available !== false && (
                        <div className="md:ml-6">
                          <Link
                            to="/contact"
                            className="block text-center bg-red-600 text-white px-6 py-3 rounded-md hover:bg-red-700 transition-colors font-semibold whitespace-nowrap"
                          >
                            DEMANDER UN DEVIS
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Prestations sur site avec matériel professionnel
            </h3>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              Nous intervenons directement dans vos locaux avec tout le matériel nécessaire. 
              Les tarifs sont dégressifs selon le nombre de participants.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="p-6 bg-red-50 rounded-lg">
                <div className="text-4xl font-bold text-red-600 mb-2">✓</div>
                <div className="text-gray-700 font-semibold">Matériel fourni</div>
              </div>
              <div className="p-6 bg-red-50 rounded-lg">
                <div className="text-4xl font-bold text-red-600 mb-2">✓</div>
                <div className="text-gray-700 font-semibold">Formateurs certifiés</div>
              </div>
              <div className="p-6 bg-red-50 rounded-lg">
                <div className="text-4xl font-bold text-red-600 mb-2">✓</div>
                <div className="text-gray-700 font-semibold">Flexibilité horaire</div>
              </div>
            </div>
            <Link 
              to="/contact"
              className="inline-block bg-red-600 text-white px-8 py-4 rounded-md hover:bg-red-700 transition-colors font-bold text-lg"
            >
              DEMANDEZ VOTRE DEVIS GRATUIT
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrestationDetail;